import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { hzAlignmentZodSchema } from "@@/shared/pages/alignments-and-placements";
import { singleContentPageBlockLayoutTypeZodSchema } from "@@/shared/pages/page-blocks/content/single-content-block/single-content.page-block.layout";
import { singleContentPageBlockStyleTypeZodSchema } from "@@/shared/pages/page-blocks/content/single-content-block/single-content.page-block.style";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import {
    asTranslatableValue,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { faCubes } from "@fortawesome/pro-solid-svg-icons";
import type { Except } from "type-fest";
import { z } from "zod";
import { contentIdZodSchema } from "../../../content/content-id";
import { pageBlockBaseZodSchema } from "../../page-block-base";
import { pageBlockIdFactory } from "../../page-block-id";

const singleContentPageBlockZodSchema = pageBlockBaseZodSchema.merge(
    z.object({
        type: z.literal("SINGLE_CONTENT_PAGE_BLOCK"),

        // Titles
        title: translatableZodSchema,
        titleColor: hexColorZodSchema.optional(),
        subtitle: translatableZodSchema,
        subtitleColor: hexColorZodSchema.optional(),
        kicker: translatableZodSchema.optional(),
        kickerColor: hexColorZodSchema.optional(),
        titleAlignment: hzAlignmentZodSchema.optional(),
        titleShadow: z.boolean(),

        //Cardcolors
        cardSolidTextColor: hexColorZodSchema.optional(),
        cardSolidBackgroundColor: hexColorZodSchema.optional(),

        // Call to action
        ctaButtonEnabled: z.boolean(),
        ctaButtonText: translatableZodSchema.optional(),
        ctaButtonUrl: z.string().optional(),
        ctaButtonTextColor: hexColorZodSchema,
        ctaButtonBackgroundColor: hexColorZodSchema,
        ctaButtonBorderColor: hexColorZodSchema,

        // General settings
        layout: singleContentPageBlockLayoutTypeZodSchema,
        style: singleContentPageBlockStyleTypeZodSchema,

        // Content
        contentId: contentIdZodSchema.nullish(),
        showInactiveContent: z.boolean().optional(),
        contentTitleOverride: translatableZodSchema.optional(),
        contentSubtitleOverride: translatableZodSchema.optional(),
        contentKickerOverride: translatableZodSchema.optional(),

        // Background
        backgroundImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
    }),
);

type SingleContentPageBlock = z.infer<typeof singleContentPageBlockZodSchema>;

const singleContentPageBlockIcon = faCubes;
type SingleContentPageBlockParams = Except<
    SingleContentPageBlock,
    "_id" | "type" | "_type" | "_createdAt"
>;
const singleContentPageBlockFactory = (
    params: SingleContentPageBlockParams,
): SingleContentPageBlock => {
    return {
        ...params,
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "SINGLE_CONTENT_PAGE_BLOCK",
        layout: params.layout ?? "leftToRight",
        style: params.style ?? "solid",
        // Texts
        title: asTranslatableValue(params.title),
        titleColor: params.titleColor,
        subtitle: asTranslatableValue(params.subtitle),
        subtitleColor: params.subtitleColor,
        kicker: asTranslatableValue(params.kicker),
        kickerColor: params.kickerColor,
        titleAlignment: params.titleAlignment || "auto",
        textColor: params.textColor,
        // Innehåll
        contentId: params.contentId,
        showInactiveContent: params.showInactiveContent ?? false,
        contentKickerOverride: asTranslatableValue(
            params.contentKickerOverride,
        ),
        contentTitleOverride: asTranslatableValue(params.contentTitleOverride),
        contentSubtitleOverride: asTranslatableValue(
            params.contentSubtitleOverride,
        ),
        // Call to action
        ctaButtonEnabled: params.ctaButtonEnabled ?? false,
        ctaButtonText: asTranslatableValue(params.ctaButtonText),
        ctaButtonBackgroundColor: params.ctaButtonBackgroundColor,
        ctaButtonBorderColor: params.ctaButtonBorderColor,
        ctaButtonTextColor: params.ctaButtonTextColor,
        ctaButtonUrl: params.ctaButtonUrl,
    };
};

const singleContentPageBlockType = "SINGLE_CONTENT_PAGE_BLOCK";

const isSingleContentPageBlock = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    input: any,
): input is SingleContentPageBlock => {
    return (
        input._type === "PAGE_BLOCK" &&
        input.type === "SINGLE_CONTENT_PAGE_BLOCK"
    );
};

export {
    isSingleContentPageBlock,
    singleContentPageBlockFactory,
    singleContentPageBlockIcon,
    singleContentPageBlockType,
    singleContentPageBlockZodSchema,
};
export type { SingleContentPageBlock };
